import React, { useEffect, useRef } from 'react';
import { Bot, ArrowUp, Loader2, AlertCircle, MessageSquare } from 'lucide-react';

/**
 * AIChat.jsx
 * Displays the Velocity AI conversation for the selected RAG session.
 * Receives sessions, messages, and selection handlers from ChatModal via props.
 */
const AIChat = ({
    messages = [],
    loadingMessages = false,
    messagesError = null,
    sendingMessage = false,
    input = '',
    onInputChange,
    onSendMessage,
    rfpId,
    activeSessionCreatedAt,
    connectionStatus,
}) => {
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom when messages update
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage?.();
        }
    };

    // ── Helpers to normalise different message shapes from the API ─────────────
    const getRole = (msg) => msg?.role ?? (msg?.sender === 'user' ? 'user' : 'assistant');
    const getContent = (msg) => msg?.content ?? msg?.text ?? msg?.message ?? '';
    const getSources = (msg) => msg?.sources ?? msg?.references ?? null;
    const getMsgId = (msg, idx) => msg?.id ?? msg?._id ?? msg?.message_id ?? idx;

    const getLocalDateString = (timestamp) => {
        if (!timestamp) return '';
        let normalized = timestamp;
        if (typeof timestamp === "string" && timestamp.includes("T") && !timestamp.endsWith("Z") && !/\+\d{2}:?\d{2}$/.test(timestamp) && !/-\d{2}:?\d{2}$/.test(timestamp)) {
            normalized = `${timestamp}Z`;
        }
        const d = new Date(normalized);
        if (isNaN(d.getTime())) return '';

        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (d.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (d.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        }

        return d.toLocaleDateString(undefined, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getLocalTimeString = (timestamp) => {
        if (!timestamp) return '';
        let normalized = timestamp;
        if (typeof timestamp === "string" && timestamp.includes("T") && !timestamp.endsWith("Z") && !/\+\d{2}:?\d{2}$/.test(timestamp) && !/-\d{2}:?\d{2}$/.test(timestamp)) {
            normalized = `${timestamp}Z`;
        }
        const d = new Date(normalized);
        if (isNaN(d.getTime())) return '';

        const formatted = d.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });

        return formatted.replace(/([ap]\.?\s?m\.?)/i, (match) => {
            return match.toUpperCase().replace(/\./g, '').replace(/\s/g, '');
        });
    };

    return (
        <main className="v-ch-main">


            {/* Message area */}
            <div className="v-ch-messages">
                {/* Loading state */}
                {loadingMessages && (
                    <div className="v-ch-ai-status">
                        <Loader2 size={18} className="v-ch-ai-spin" />
                        <span>Loading conversation…</span>
                    </div>
                )}

                {/* Error state */}
                {messagesError && !loadingMessages && (
                    <div className="v-ch-ai-error">
                        <AlertCircle size={16} />
                        <span>{messagesError}</span>
                    </div>
                )}

                {/* Empty state — no rfpId */}
                {!rfpId && !loadingMessages && (
                    <div className="v-ch-ai-empty">
                        <MessageSquare size={32} />
                        <p>Open an RFP to start chatting with Velocity AI.</p>
                    </div>
                )}

                {/* Empty state — session loaded but no messages */}
                {rfpId && !loadingMessages && !messagesError && messages.length === 0 && (
                    <div className="v-ch-ai-empty">
                        <Bot size={32} />
                        <p>No messages yet. Ask Velocity AI something!</p>
                    </div>
                )}

                {/* Render messages */}
                {(() => {
                    let lastDateStr = null;
                    return messages.map((msg, idx) => {
                        const role = getRole(msg);
                        const isUser = role === 'user' || role === 'human';
                        const content = getContent(msg);

                        const rawDate = msg?.created_at ?? msg?.timestamp ?? activeSessionCreatedAt;
                        const dateStr = getLocalDateString(rawDate);

                        let dateSeparator = null;
                        if (dateStr && dateStr !== lastDateStr) {
                            lastDateStr = dateStr;
                            dateSeparator = (
                                <div className={`v-ch-date-separator ${dateStr && dateStr.toLowerCase() === 'today' ? 'v-ch-date-separator--no-line' : ''}`}>
                                    <span className="v-ch-date-separator-text">{dateStr}</span>
                                </div>
                            );
                        }

                        return (
                            <React.Fragment key={getMsgId(msg, idx)}>
                                {dateSeparator}
                                <div className={`v-ch-msg ${isUser ? 'v-ch-msg--self' : ''}`} style={isUser ? { flexDirection: 'row', alignSelf: 'flex-end', justifyContent: 'flex-end', gap: '8px', maxWidth: '85%' } : { gap: '8px', maxWidth: '85%' }}>
                                    {!isUser && (
                                        <div
                                            className="v-ch-avatar"
                                            style={{ background: '#E9471D', flexShrink: 0, marginBottom: '2px' }}
                                        >
                                            <Bot size={16} color="#fff" />
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', maxWidth: '85%', minWidth: 0 }}>
                                        <div style={{
                                            marginBottom: '4px',
                                            fontSize: '11px',
                                            color: '#94A3B8',
                                            display: 'flex',
                                            gap: '8px',
                                            alignItems: 'center',
                                            justifyContent: isUser ? 'flex-end' : 'flex-start',
                                            width: '100%'
                                        }}>
                                            {!isUser && <span style={{ fontWeight: '600', color: '#64748B' }}>Velocity AI</span>}
                                            <span>{getLocalTimeString(rawDate)}</span>
                                        </div>
                                        {isUser ? (
                                            <div className="v-ch-bubble">
                                                {content}
                                            </div>
                                        ) : (
                                            <div className="v-ch-bubble" style={{ background: '#F1F5F9', color: '#1E293B' }}>
                                                {content === '' ? (
                                                    <div className="v-ch-typing-indicator-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                                                        <div className="typing-dots" style={{ display: 'flex', gap: '3px' }}>
                                                            <span className="dot" style={{ width: '4.5px', height: '4.5px', background: '#E9471D', borderRadius: '50%', display: 'inline-block' }} />
                                                            <span className="dot" style={{ width: '4.5px', height: '4.5px', background: '#E9471D', borderRadius: '50%', display: 'inline-block' }} />
                                                            <span className="dot" style={{ width: '4.5px', height: '4.5px', background: '#E9471D', borderRadius: '50%', display: 'inline-block' }} />
                                                        </div>
                                                        <span style={{ fontSize: '11px', color: '#64748B', fontStyle: 'italic', fontWeight: '500' }}>Velocity AI is thinking...</span>
                                                    </div>
                                                ) : (
                                                    content
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    });
                })()}

                {/* Scroll anchor */}
                <div ref={messagesEndRef} />
            </div>

            {/* Input bar */}
            <div className="v-ch-input-bar">
                <div className="v-ch-input-composer">
                    <div className="v-ch-input-wrap">
                        <input
                            type="text"
                            placeholder={sendingMessage ? "Velocity AI is thinking..." : "Ask Velocity AI…"}
                            value={input}
                            onChange={(e) => onInputChange?.(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={!rfpId || sendingMessage}
                        />
                    </div>
                    <div className="v-ch-input-actions-right">
                        <button
                            className="v-ch-send-btn premium-send"
                            aria-label="Send message"
                            onClick={() => onSendMessage?.()}
                            disabled={!rfpId || !input.trim() || sendingMessage}
                        >
                            <ArrowUp size={16} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default AIChat;
